// B-101 — "Cuadre del día", in a day's detail: the operator's CUENTA and NOTAS PENDIENTES sheets.
//
// B-108 — laid out for daily use, wide, read top to bottom by what matters first:
//   1. four figures: the offices' total, the account, the difference and what is left unexplained
//      (the residual, red when no note explains it, green when the notes do);
//   2. two columns: the pending notes open that day — filtered by kind, office and age, each with
//      its days open — and beside them what to look at, the offices without a balance and the
//      account rolled forward;
//   3. opened on demand: the control totals (open on their own when one does not hold), the days
//      before and the differences for CNN.
// Moving to another day is the page's (`NavegadorDeDias`, beside the breadcrumb), not this dialog's:
// a day of the history changes the page's date (`onCambiarFecha`). An office — a note's or one with
// no balance — opens its closing's detail (`onVerOficina`); the panel knows how to find it. The day
// exports to Excel (`hojasDelCuadre`).
//
// Every figure comes from `getCierreDayReconciliation`; nothing is computed here but a note's age.
// Shown for a client with a day reconciliation (the backend answers 404 for any other, and the button
// is then not drawn) and to whoever sees the whole client, since the total sums every office.
// B-103 — `DiferenciasCnn`; B-104 — "Descargar Drive de mañana" (`DescargarDriveManana`).
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CheckCircle2, ChevronDown, Download, Scale,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Spinner,
  cn,
} from "@mantis-sas/ui";

import { getCierreClosingDates, getCierreDayReconciliation } from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { fechaDMY, montoLegible } from "../../../utils/cierrePanel";
import { getColombiaDateString } from "../../../utils/dateUtils";
import { descargarTablas } from "../../../utils/excelTabla";
import {
  DIAS_VIEJA,
  MOTIVO_FALTANTE,
  controlesOrdenados,
  cuentaCalculada,
  diasAbierta,
  filtrarPendientes,
  fueraDeTolerancia,
  hayFiltros,
  hojasDelCuadre,
  nombreArchivoDelCuadre,
  opcionesDePendientes,
  porQueSinCuenta,
  textoDias,
  tipoDePendiente,
  veredictoDelDia,
} from "../../../utils/cierreCuadreDia";
import { DiferenciasCnn } from "./DiferenciasCnn";
import { DescargarDriveManana } from "./DescargarDriveManana";
import { FiltrosPendientes } from "./FiltrosPendientes";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const TH = "px-3 py-2 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground";
const TD = "px-3 py-2";
const MONTO = "px-3 py-2 text-right tabular-nums";
const FOCO = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const DIAS_HISTORIAL = 7;

const ESTILO_ESTADO = {
  ok: "border-brinks-success-border bg-brinks-success-soft text-brinks-success",
  mal: "border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger",
  neutro: "border-brinks-border bg-brinks-surface text-brinks-muted-foreground",
};

const Estado = ({ tono, children }) => (
  <span className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold", ESTILO_ESTADO[tono])}>
    {children}
  </span>
);

// One of the day's four figures.
function Cifra({ etiqueta, valor, detalle, tono, ayuda }) {
  return (
    <div
      {...ayuda}
      className={cn(
        "rounded-[10px] border bg-card px-3.5 py-3",
        tono === "mal" && "border-brinks-danger-border bg-brinks-danger-soft",
        tono === "ok" && "border-brinks-success-border bg-brinks-success-soft",
      )}
    >
      <dt className={cn("text-[11.5px] font-medium text-brinks-muted-foreground", tono === "mal" && "text-brinks-danger", tono === "ok" && "text-brinks-success")}>
        {etiqueta}
      </dt>
      <dd className={cn("mt-1 text-[19px] font-semibold leading-tight tabular-nums text-brinks-foreground", tono === "mal" && "text-brinks-danger", tono === "ok" && "text-brinks-success")}>
        {valor}
      </dd>
      {detalle ? <dd className="mt-1 text-[11px] text-brinks-muted-foreground">{detalle}</dd> : null}
    </div>
  );
}

// One block of the day: a heading (with what it says at a glance) over its own content.
function Tarjeta({ titulo, extra, children, className, ayuda }) {
  return (
    <section {...ayuda} className={cn("overflow-hidden rounded-[10px] border bg-card", className)}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b bg-brinks-surface px-3 py-2">
        <h4 className="text-[13px] font-semibold text-brinks-foreground">{titulo}</h4>
        {extra}
      </header>
      {children}
    </section>
  );
}

// A block that is summarised in its heading and opened on demand — a real button, so Enter and
// Space open it and a screen reader hears whether it is open.
function Desplegable({ id, titulo, resumen, abierto, onToggle, children, ayuda }) {
  return (
    <section {...ayuda} className="overflow-hidden rounded-[10px] border bg-card">
      <h4>
        <button
          type="button"
          aria-expanded={abierto}
          aria-controls={id}
          onClick={onToggle}
          className={cn("flex w-full flex-wrap items-center justify-between gap-2 bg-brinks-surface px-3 py-2 text-left hover:bg-brinks-surface-hover", FOCO)}
        >
          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-brinks-foreground">
            <ChevronDown className={cn("h-4 w-4 transition-transform", !abierto && "-rotate-90")} />
            {titulo}
          </span>
          {resumen}
        </button>
      </h4>
      {abierto ? <div id={id} className="border-t">{children}</div> : null}
    </section>
  );
}

// A line of a running account: what it is, and its amount at the right edge.
function Linea({ etiqueta, valor, total = false, tono }) {
  return (
    <tr className={cn("border-b last:border-0", total && "bg-brinks-surface/60 font-semibold")}>
      <td className={TD}>{etiqueta}</td>
      <td className={cn(MONTO, tono === "mal" && "font-semibold text-brinks-danger")}>{valor}</td>
    </tr>
  );
}

// An office as a link to its closing's detail, when the panel can open it.
function Oficina({ codigo, nombre, fecha, onVer }) {
  const texto = (
    <>
      <span className="tabular-nums text-brinks-muted-foreground">{codigo}</span> {nombre || ""}
    </>
  );
  if (!onVer) return <span>{texto}</span>;
  return (
    <button
      type="button"
      className={cn("rounded-[4px] text-left underline-offset-2 hover:underline", FOCO)}
      aria-label={`Ver el cierre de ${codigo} ${nombre || ""} del ${fechaDMY(fecha)}`.trim()}
      onClick={() => onVer(codigo, fecha)}
    >
      {texto}
    </button>
  );
}

export function CuadreDelDia({
  token, mantisBusinessId, clientCode, fecha, clienteNombre, onVerOficina, onCambiarFecha,
}) {
  const [disponible, setDisponible] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [dia, setDia] = useState(null);
  const [historial, setHistorial] = useState(null); // [{ fecha, dia }] once asked
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState({});
  const [abiertos, setAbiertos] = useState({});
  const [exportando, setExportando] = useState(false);
  const hoy = useMemo(() => getColombiaDateString(), []);

  // Asked once for the day: whether this client has a day reconciliation at all decides the button.
  useEffect(() => {
    let activo = true;
    setDisponible(false);
    setHistorial(null);
    if (!clientCode || !fecha) return undefined;
    getCierreDayReconciliation(token, mantisBusinessId, clientCode, fecha)
      .then((res) => {
        if (!activo) return;
        if (res?.success) {
          setDia(res.data);
          setDisponible(true);
        }
      })
      .catch(() => {});
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode, fecha]);

  const abrir = async () => {
    setAbierto(true);
    setCargando(true);
    setError(null);
    setFiltros({});
    try {
      const res = await getCierreDayReconciliation(token, mantisBusinessId, clientCode, fecha);
      if (res?.success) setDia(res.data);
      else setError(correctionError(res));
    } catch (e) {
      // B-126 — not the figures the button was drawn with: they may be stale. Said, with a retry.
      setDia(null);
      setError("No se pudo cargar el cuadre del día.");
    }
    setCargando(false);
  };

  // The days up to this one, asked only once the history is opened: the dates the client has, and
  // each one's figures.
  useEffect(() => {
    if (!abierto || !abiertos.historial || historial) return undefined;
    let activo = true;
    (async () => {
      const r = await getCierreClosingDates(token, mantisBusinessId, clientCode, { until: fecha, limit: DIAS_HISTORIAL }).catch(() => null);
      const fechas = r?.success ? (r.data?.dates || []).map((d) => d.businessDate) : [];
      const dias = await Promise.all(fechas.map(async (f) => {
        if (f === fecha) return { fecha: f, dia };
        const res = await getCierreDayReconciliation(token, mantisBusinessId, clientCode, f).catch(() => null);
        return { fecha: f, dia: res?.success ? res.data : null };
      }));
      if (activo) setHistorial(dias);
    })();
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, abiertos.historial]);

  if (!disponible) return null;

  const pendientes = dia?.pendientesAbiertos || [];
  const visibles = filtrarPendientes(pendientes, filtros, hoy);
  const opciones = opcionesDePendientes(pendientes);
  const alertas = dia?.alertas || [];
  const faltantes = dia?.oficinasFaltantes || [];
  const residuoMal = alertas.some((a) => a.key === "diferencia_no_explicada");
  // The residual is the fourth figure; the rest is what to look at.
  const porRevisar = alertas.filter((a) => a.key !== "diferencia_no_explicada");
  const sinTotal = dia?.totalOficinas === null || dia?.totalOficinas === undefined;
  const calculada = cuentaCalculada(dia);
  const sinCuenta = porQueSinCuenta(dia);
  const veredicto = veredictoDelDia(dia);
  const controles = controlesOrdenados(dia?.controles);
  const controlesMal = controles.filter((c) => c.holds === false).length;
  const cuentaMal = !calculada && fueraDeTolerancia(dia?.diferenciaCuenta, 1);
  // A block with a problem opens itself; one that is fine waits to be asked for.
  const estaAbierto = (clave, porDefecto) => (abiertos[clave] === undefined ? porDefecto : abiertos[clave]);
  const alternar = (clave, porDefecto) => setAbiertos((a) => ({ ...a, [clave]: !estaAbierto(clave, porDefecto) }));
  const ver = onVerOficina
    ? (codigo, f) => {
      setAbierto(false);
      onVerOficina(codigo, f);
    }
    : null;
  const irA = onCambiarFecha
    ? (f) => {
      setAbierto(false);
      onCambiarFecha(f);
    }
    : null;

  const exportar = async () => {
    if (!dia) return;
    setExportando(true);
    try {
      await descargarTablas(nombreArchivoDelCuadre(fecha), hojasDelCuadre({
        dia, fecha, cliente: clienteNombre, hoy, historial: (historial || []).filter((h) => h.fecha !== fecha && h.dia),
      }));
    } catch (e) {
      setError("No se pudo descargar el Excel.");
    }
    setExportando(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={abrir}>
        <Scale className="h-4 w-4 shrink-0" /> <span className="truncate">Cuadre del día</span>
      </Button>
      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="brinks-scope font-redhat flex max-h-[92vh] w-[calc(100vw-1rem)] max-w-[1180px] flex-col gap-0 overflow-hidden p-0 sm:w-[calc(100vw-3rem)]">
          {/* The right padding leaves the dialog's close button its own corner. */}
          <DialogHeader className="border-b px-4 py-3.5 pr-16 text-left sm:px-5 sm:pr-14">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="hidden h-10 w-10 shrink-0 place-items-center rounded-[10px] border bg-card text-brinks-foreground shadow-brinks-card sm:grid">
                <Scale className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex min-w-0 items-center gap-1">
                    <DialogTitle className="text-[17px] font-semibold text-brinks-foreground">Cuadre del día {fechaDMY(fecha)}</DialogTitle>
                    <BotonGuia guiaId="dialogo-cuadre-del-dia" titulo="Cuadre del día" texto="Compara la suma de los saldos de las oficinas con la cuenta del corresponsal y muestra qué notas pendientes explican la diferencia." />
                  </div>
                  {dia && !cargando ? <Estado tono={veredicto.tono}>{veredicto.texto}</Estado> : null}
                </div>
                <DialogDescription className="mt-0.5 text-[12.5px] text-brinks-muted-foreground">
                  La suma de los saldos de las oficinas contra la cuenta del corresponsal, y las notas pendientes que explican la diferencia.
                </DialogDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button {...guia("Exportar Excel", "Descarga el cuadre de este día en Excel: las cifras, las notas pendientes y los controles.")} variant="outline" size="sm" className="h-8 gap-1.5" disabled={!dia || cargando || exportando} onClick={exportar}>
                  {exportando ? <Spinner size="sm" /> : <Download className="h-4 w-4" />} Exportar Excel
                </Button>
                {dia && !cargando ? (
                  <DescargarDriveManana token={token} mantisBusinessId={mantisBusinessId} clientCode={clientCode} fecha={fecha} />
                ) : null}
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-background px-4 py-4 text-[12.5px] text-brinks-foreground sm:px-5">
            {cargando ? (
              <div className="flex justify-center py-10"><Spinner /></div>
            ) : null}
            {error ? (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-3 py-2 text-brinks-danger">
                <span>{error}</span>
                {/* B-126 — a network failure is not the end of it. */}
                {!dia ? <Button variant="outline" size="sm" className="h-7" onClick={abrir}>Reintentar</Button> : null}
              </div>
            ) : null}

            {!cargando && dia ? (
              <>
                {!dia.calculado ? (
                  <p className="rounded-[8px] border bg-brinks-surface px-3 py-2 text-brinks-muted-foreground">
                    Este día todavía no se ha procesado: las cifras aparecen al procesarlo.
                  </p>
                ) : null}

                {/* 1. The day in four figures, the residual last and loud. */}
                <dl aria-label="Diferencias contables" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <Cifra ayuda={guia("Total saldo de las oficinas", "La suma de los saldos finales de todas las oficinas ese día. Dice \"Sin calcular\" mientras falte el saldo de alguna.")} etiqueta="Total saldo de las oficinas" valor={sinTotal ? "Sin calcular" : montoLegible(dia.totalOficinas)} />
                  <Cifra ayuda={guia("Cuenta del corresponsal", "El saldo de la cuenta del corresponsal ese día, con el que se compara el total de las oficinas.")} etiqueta="Cuenta del corresponsal" valor={montoLegible(dia.cuenta)} />
                  <Cifra
                    ayuda={guia("Diferencia", "Cuenta del corresponsal menos el total de las oficinas. Debajo, cuánto suman las notas pendientes abiertas que la pueden explicar.")}
                    etiqueta="Diferencia"
                    valor={montoLegible(dia.diferencia)}
                    detalle={`Notas pendientes abiertas (${pendientes.length}): ${montoLegible(dia.totalPendientes)}`}
                  />
                  <Cifra
                    ayuda={guia("Sin explicar", "Lo que queda de la diferencia después de las notas pendientes. En rojo, ninguna nota lo explica y hay que revisarlo; en verde, las notas lo explican.")}
                    etiqueta={residuoMal ? "Sin explicar (ninguna nota lo explica)" : "Sin explicar"}
                    valor={montoLegible(dia.residuo)}
                    detalle={dia.calculado && !sinTotal
                      ? (residuoMal ? "La diferencia no está explicada por las notas pendientes" : "Las notas pendientes explican la diferencia")
                      : null}
                    tono={!dia.calculado || sinTotal ? undefined : residuoMal ? "mal" : "ok"}
                  />
                </dl>

                {/* 2. The notes on the left; what to look at and the account on the right. */}
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
                  <Tarjeta
                    ayuda={guia("Notas pendientes abiertas", "Las notas que siguen abiertas ese día, con su oficina, tipo, días abierta y valor. Filtra por tipo, oficina o antigüedad, y toca una oficina para abrir su cierre.")}
                    titulo="Notas pendientes abiertas"
                    extra={(
                      <span className="text-[11.5px] tabular-nums text-brinks-muted-foreground">
                        {hayFiltros(filtros) ? `${visibles.length} de ${pendientes.length}` : pendientes.length}
                      </span>
                    )}
                  >
                    {pendientes.length > 1 ? (
                      <div className="border-b px-3 py-2">
                        <FiltrosPendientes filtros={filtros} onChange={setFiltros} opciones={opciones} campos={["tipo", "oficina", "antiguedad"]} />
                      </div>
                    ) : null}
                    {visibles.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[560px] border-collapse" aria-label="Pendientes abiertos">
                          <thead>
                            <tr className="border-b text-left">
                              <th className={TH}>Oficina</th>
                              <th className={TH}>Tipo</th>
                              <th className={TH}>Abierta desde</th>
                              <th className={cn(TH, "text-right")}>Días</th>
                              <th className={cn(TH, "text-right")}>Valor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibles.map((item) => {
                              const diasAbiertos = diasAbierta({ ...item, status: "OPEN" }, hoy);
                              return (
                                <tr key={item.id} className="border-b hover:bg-brinks-surface-hover/50">
                                  <td className={TD}>
                                    <Oficina codigo={item.officeCode} nombre={item.officeName} fecha={fecha} onVer={ver} />
                                  </td>
                                  <td className={TD}>{tipoDePendiente(item.kind)}</td>
                                  <td className={cn(TD, "tabular-nums")}>{fechaDMY(item.openedOn)}</td>
                                  <td className={cn(TD, "text-right tabular-nums", diasAbiertos > DIAS_VIEJA && "font-semibold text-brinks-warning")}>
                                    {textoDias(diasAbiertos)}
                                  </td>
                                  <td className={MONTO}>{montoLegible(item.amount)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="bg-brinks-surface/60 font-semibold">
                              <td className={TD} colSpan={4}>{hayFiltros(filtros) ? "Total de todas las notas" : "Total"}</td>
                              <td className={MONTO}>{montoLegible(dia.totalPendientes)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <p className="px-3 py-3 text-brinks-muted-foreground">
                        {pendientes.length ? "Ninguna nota cumple los filtros." : "No hay notas pendientes abiertas este día."}
                      </p>
                    )}
                  </Tarjeta>

                  <div className="space-y-4">
                    {porRevisar.length ? (
                      <div {...guia("Para revisar", "Lo que no cuadra ese día y hay que mirar: los totales de control que no coinciden y por cuánto.")} className="rounded-[10px] border border-brinks-warning-border bg-brinks-warning-soft px-3 py-2.5 text-brinks-warning">
                        <p className="mb-1 flex items-center gap-1.5 font-semibold">
                          <AlertTriangle className="h-4 w-4" /> Para revisar
                        </p>
                        <ul className="list-disc space-y-0.5 pl-6">
                          {porRevisar.map((alerta) => <li key={alerta.key}>{alerta.message}</li>)}
                        </ul>
                      </div>
                    ) : null}

                    {faltantes.length ? (
                      <Tarjeta ayuda={guia("Oficinas sin saldo final", "Las oficinas que todavía no tienen saldo final ese día. Mientras falten, el total de las oficinas queda sin calcular. Toca una para abrir su cierre.")} titulo="Oficinas sin saldo final" extra={<Estado tono="mal">{faltantes.length}</Estado>}>
                        <p className="border-b px-3 py-2 text-brinks-muted-foreground">Mientras falten, el total de las oficinas queda sin calcular.</p>
                        <ul className="divide-y">
                          {faltantes.map((o) => (
                            <li key={o.officeCode} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                              <Oficina codigo={o.officeCode} nombre={o.officeName} fecha={fecha} onVer={ver} />
                              <span className="text-brinks-muted-foreground">{MOTIVO_FALTANTE[o.reason] || o.reason}</span>
                            </li>
                          ))}
                        </ul>
                      </Tarjeta>
                    ) : null}

                    <Tarjeta
                      ayuda={guia("Cuenta del corresponsal", "Cómo se llega al saldo del día: el saldo del día anterior más y menos los movimientos del día.")}
                      titulo="Cuenta del corresponsal"
                      extra={sinCuenta || cuentaMal
                        ? <Estado tono="mal">No cuadra</Estado>
                        : !calculada && dia.diferenciaCuenta !== null && dia.diferenciaCuenta !== undefined
                          ? <Estado tono="ok"><CheckCircle2 className="h-3 w-3" /> Cuadra</Estado>
                          : null}
                    >
                      {calculada ? (
                        <p className="border-b px-3 py-2 text-[11.5px] text-brinks-muted-foreground">
                          Calculada como en la hoja CUENTA: el saldo del día anterior y los movimientos del banco del día.
                        </p>
                      ) : null}
                      {sinCuenta ? (
                        <p role="status" className="border-b px-3 py-2 text-brinks-warning">{sinCuenta}</p>
                      ) : null}
                      <table className="w-full border-collapse" aria-label="Cuadre de la cuenta">
                        <tbody>
                          <Linea etiqueta="Saldo del día anterior" valor={montoLegible(dia.saldoCuentaAnterior)} />
                          {(dia.movimientosCuenta || []).map((mov) => (
                            <Linea key={mov.key} etiqueta={`${mov.sign < 0 ? "− " : "+ "}${mov.label}`} valor={montoLegible(mov.value)} />
                          ))}
                          <Linea etiqueta={calculada ? "Saldo del día" : "Saldo esperado"} valor={montoLegible(dia.saldoCuentaEsperado)} total />
                          {calculada ? null : (
                            <Linea etiqueta="Cuenta real − esperado" valor={montoLegible(dia.diferenciaCuenta)} tono={cuentaMal ? "mal" : undefined} />
                          )}
                        </tbody>
                      </table>
                    </Tarjeta>
                  </div>
                </div>

                {/* 3. Opened on demand. */}
                <Desplegable
                  ayuda={guia("Control de totales", "Compara los totales de cada concepto (depósitos, retiros, pagos…) entre el archivo del banco y la suma de las oficinas. Se abre solo cuando alguno no cuadra.")}
                  id="cuadre-controles"
                  titulo="Control de totales"
                  resumen={controlesMal
                    ? <Estado tono="mal">{controlesMal === 1 ? "1 no cuadra" : `${controlesMal} no cuadran`}</Estado>
                    : controles.length ? <Estado tono="ok"><CheckCircle2 className="h-3 w-3" /> Todo cuadra</Estado> : null}
                  abierto={estaAbierto("controles", controlesMal > 0)}
                  onToggle={() => alternar("controles", controlesMal > 0)}
                >
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse" aria-label="Control de totales">
                      <thead>
                        <tr className="border-b text-left">
                          <th className={TH}>Concepto</th>
                          <th className={cn(TH, "text-right")}>Oficinas</th>
                          <th className={cn(TH, "text-right")}>Banco</th>
                          <th className={cn(TH, "text-right")}>Diferencia</th>
                          <th className={cn(TH, "text-right")}>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {controles.map((control) => {
                          const valor = (v) => (control.format === "count" ? (v ?? "—") : montoLegible(v));
                          return (
                            <tr key={control.key} className="border-b last:border-0">
                              <td className={TD}>
                                {control.label}
                                {control.holds === null ? (
                                  <span className="block text-[11.5px] text-brinks-muted-foreground">Faltan {control.missingOffices.join(", ")}</span>
                                ) : null}
                              </td>
                              <td className={cn(TD, "text-right tabular-nums")}>{valor(control.left)}</td>
                              <td className={cn(TD, "text-right tabular-nums")}>{valor(control.right)}</td>
                              <td className={cn(TD, "text-right tabular-nums", control.holds === false && "font-semibold text-brinks-danger")}>
                                {valor(control.difference)}
                              </td>
                              <td className={cn(TD, "text-right")}>
                                {control.holds === true ? (
                                  <Estado tono="ok">Cuadra</Estado>
                                ) : control.holds === false ? (
                                  <Estado tono="mal">No cuadra</Estado>
                                ) : (
                                  <Estado tono="neutro">Sin revisar</Estado>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Desplegable>

                <Desplegable
                  ayuda={guia("Últimos días", "Cómo venían los días anteriores. Toca un día para ir a su cuadre.")}
                  id="cuadre-historial"
                  titulo={`Últimos ${DIAS_HISTORIAL} días`}
                  resumen={<span className="text-[11.5px] text-brinks-muted-foreground">{irA ? "Elige un día para ir a él" : "Cómo venían los días anteriores"}</span>}
                  abierto={estaAbierto("historial", false)}
                  onToggle={() => alternar("historial", false)}
                >
                  {historial ? (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[640px] border-collapse" aria-label="Historial de días">
                        <thead>
                          <tr className="border-b text-left">
                            <th className={TH}>Fecha</th>
                            <th className={cn(TH, "text-right")}>Total oficinas</th>
                            <th className={cn(TH, "text-right")}>Cuenta</th>
                            <th className={cn(TH, "text-right")}>Diferencia</th>
                            <th className={cn(TH, "text-right")}>Sin explicar</th>
                            <th className={cn(TH, "text-right")}>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historial.map(({ fecha: f, dia: d }) => {
                            const v = veredictoDelDia(d);
                            return (
                              <tr key={f} className={cn("border-b last:border-0", f === fecha && "bg-brinks-surface")}>
                                <td className={TD}>
                                  {f === fecha || !irA ? (
                                    <span className={cn("tabular-nums", f === fecha && "font-semibold")} aria-current={f === fecha ? "date" : undefined}>{fechaDMY(f)}</span>
                                  ) : (
                                    <button type="button" className={cn("rounded-[4px] tabular-nums underline-offset-2 hover:underline", FOCO)} onClick={() => irA(f)}>
                                      {fechaDMY(f)}
                                    </button>
                                  )}
                                </td>
                                <td className={cn(TD, "text-right tabular-nums")}>{d ? montoLegible(d.totalOficinas) : "—"}</td>
                                <td className={cn(TD, "text-right tabular-nums")}>{d ? montoLegible(d.cuenta) : "—"}</td>
                                <td className={cn(TD, "text-right tabular-nums")}>{d ? montoLegible(d.diferencia) : "—"}</td>
                                <td className={cn(TD, "text-right tabular-nums", v.tono === "mal" && "font-semibold text-brinks-danger")}>
                                  {d ? montoLegible(d.residuo) : "—"}
                                </td>
                                <td className={cn(TD, "text-right")}><Estado tono={v.tono}>{v.texto}</Estado></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex justify-center py-6"><Spinner /></div>
                  )}
                </Desplegable>

                <DiferenciasCnn token={token} mantisBusinessId={mantisBusinessId} clientCode={clientCode} fecha={fecha} />
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CuadreDelDia;
